import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, Stamp, PenLine, Save, FileCheck } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function VistaPreviaDocumento({ trialId, templateCodigo, documento = null, onClose, onSaved }) {
  const [meta, setMeta] = useState(null);
  const [values, setValues] = useState({});
  const [checks, setChecks] = useState(documento?.checks_marcados || []);
  const [saving, setSaving] = useState(false);

  const tId = documento?.trial_id || trialId;
  const tCodigo = documento?.template_codigo || templateCodigo;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/documentos/preview`, {
          params: { template_codigo: tCodigo, trial_id: tId },
          withCredentials: true,
        });
        setMeta(r.data);
        const base = {};
        (r.data.campos_editables || []).forEach((c) => {
          base[c.key] = documento?.campos_especificos?.[c.key]
            ?? documento?.contexto?.[c.key]
            ?? r.data.contexto?.[c.key]
            ?? '';
        });
        setValues(base);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error al cargar la vista previa');
        onClose();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCheck = (op) =>
    setChecks((prev) => (prev.includes(op) ? prev.filter((c) => c !== op) : [...prev, op]));

  const validar = () => {
    const faltan = (meta.campos_editables || []).filter(
      (c) => c.obligatorio && !String(values[c.key] || '').trim()
    );
    if (faltan.length) {
      toast.error(`Campos obligatorios: ${faltan.map((c) => c.label).join(', ')}`);
      return false;
    }
    return true;
  };

  const guardar = async (generar) => {
    if (generar && !validar()) return;
    setSaving(true);
    try {
      let docId = documento?.id;
      const payload = { campos_especificos: values, checks_marcados: checks };
      if (docId) {
        await axios.put(`${API_URL}/api/documentos/${docId}`, payload, { withCredentials: true });
      } else {
        const r = await axios.post(`${API_URL}/api/documentos/borrador`,
          { template_codigo: tCodigo, trial_id: tId, ...payload }, { withCredentials: true });
        docId = r.data.id;
      }
      if (generar) {
        await axios.post(`${API_URL}/api/documentos/${docId}/generar`, {}, { withCredentials: true });
        toast.success('Documento generado · pendiente de firma');
      } else {
        toast.success('Borrador guardado');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar el documento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="vista-previa-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] font-light text-xl">
            Vista previa editable · {meta?.template?.nombre || '...'}
          </DialogTitle>
        </DialogHeader>

        {!meta ? (
          <p className="text-sm text-zinc-500 py-8 text-center">Cargando datos de la ficha...</p>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/10 border border-amber-500/25">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200/80">
                Los cambios realizados aquí solo afectan a <b>este documento</b>. La ficha del cliente en el CRM no se modifica.
              </p>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="firmantes-requeridos">
              {(meta.firmantes_requeridos || []).map((f) => (
                <span key={f.rol} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border ${
                  f.sello_automatico
                    ? 'bg-sky-500/10 text-sky-300 border-sky-500/25'
                    : 'bg-white/[0.04] text-zinc-300 border-white/10'
                }`}>
                  {f.sello_automatico ? <Stamp className="w-3 h-3" /> : <PenLine className="w-3 h-3" />}
                  {f.rol} · {f.sello_automatico ? 'Sello automático' : 'Firma requerida'}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(meta.campos_editables || []).map((c) => (
                <div key={c.key} className={c.tipo === 'textarea' ? 'sm:col-span-2' : ''}>
                  <label className="text-xs text-zinc-500 mb-1.5 block">
                    {c.label}{c.obligatorio && <span className="text-amber-400"> *</span>}
                  </label>
                  {c.tipo === 'textarea' ? (
                    <Textarea
                      value={values[c.key] || ''}
                      onChange={(e) => setValues((v) => ({ ...v, [c.key]: e.target.value }))}
                      className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 min-h-[70px]"
                      data-testid={`preview-campo-${c.key}`}
                    />
                  ) : (
                    <Input
                      value={values[c.key] || ''}
                      onChange={(e) => setValues((v) => ({ ...v, [c.key]: e.target.value }))}
                      className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                      data-testid={`preview-campo-${c.key}`}
                    />
                  )}
                </div>
              ))}
            </div>

            {(meta.checks_grupos || []).map((g) => (
              <div key={g.titulo}>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{g.titulo}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {g.opciones.map((op) => (
                    <button
                      key={op}
                      onClick={() => toggleCheck(op)}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-left text-xs transition-colors ${
                        checks.includes(op)
                          ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
                          : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:border-white/25'
                      }`}
                      data-testid={`preview-check-${op.slice(0, 20).replace(/\s+/g, '-')}`}
                    >
                      <span className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center text-[9px] ${
                        checks.includes(op) ? 'border-emerald-400 text-emerald-300' : 'border-zinc-600'
                      }`}>
                        {checks.includes(op) ? '✓' : ''}
                      </span>
                      {op}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={onClose} className="border-white/20 text-zinc-300">Cancelar</Button>
              <Button
                variant="outline"
                onClick={() => guardar(false)}
                disabled={saving}
                className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10"
                data-testid="preview-guardar-borrador-btn"
              >
                <Save className="w-4 h-4 mr-1.5" />Guardar borrador
              </Button>
              <Button
                onClick={() => guardar(true)}
                disabled={saving}
                className="bg-white text-black hover:bg-zinc-200"
                data-testid="preview-generar-btn"
              >
                <FileCheck className="w-4 h-4 mr-1.5" />
                {saving ? 'Procesando...' : 'Generar documento'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
